<template>
  <div v-if="isPending" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
    <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
    <p class="text-sm text-center text-slate-500 pt-4">
      Loading...
    </p>
  </div>

  <div v-else-if="!isPending && !isError">
    <form @submit.prevent="form.handleSubmit">
      <div class="w-full max-w-xl flex flex-col gap-4 sm:gap-8">
        <form.Field
          name="companyAddress"
          :validators="{
            onBlur: yup.string().required('Company Address is required'),
          }"
        >
          <template #default="{ field }">
            <BaseInput
              v-model="formData.companyAddress"
              label="Company Address"
              :name="field.name"
              placeholder="Company Address"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="defaultMargin"
          :validators="{
            onBlur: yup.number().transform((value) => isNaN(Number(value)) ? -1 : Number(value)).required('Default Markup % (Margin) is required').min(0, 'Must be greater than or equal to 0').max(99, 'Must be less than or equal to 99'),
          }"
        >
          <template #default="{ field }">
            <BaseInput
              v-model.number="formData.defaultMargin"
              :name="field.name"
              type="number"
              step="any"
              label="Default Markup % (Margin)"
              placeholder="Default Markup % (Margin)"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="defaultTax"
          :validators="{
            onBlur: yup.number().transform((value) => isNaN(Number(value)) ? -1 : Number(value)).required('Default Tax % is required').min(0, 'Must be greater than or equal to 0').max(99, 'Must be less than or equal to 99'),
          }"
        >
          <template #default="{ field }">
            <BaseInput
              v-model.number="formData.defaultTax"
              :name="field.name"
              type="number"
              step="any"
              label="Default Tax %"
              placeholder="Default Tax %"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <div>
          <form.Field
            name="contractTerms"
            :validators="{
              onChange: yup.string().transform((value) => value.replace(/<[^>]*>/g, '')).required('Terms and Conditions is required')
            }"
          >
            <template #default="{ field }">
              <Editor
                v-model="formData.contractTerms"
                :name="field.name"
                label="Contract Terms &amp; Conditions"
                class="min-h-28"
                @update:model-value="(value) => field.handleChange(value)"
              />
              <!-- errors -->
              <div v-if="field.state.meta.errors" class="text-red-500 text-sm mt-1">
                {{ field.state.meta.errors[0] }}
              </div>
            </template>
          </form.Field>
        </div>

        <!-- auto save settings -->
        <div class="border-t border-slate-200 pt-4 mt-4 flex flex-col gap-4">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">Auto Save Settings</h3>
            <p class="text-sm text-slate-500 mt-1">
              Enable auto save for budgets/proposals to prevent data loss.
            </p>
          </div>

          <div class="flex items-center gap-4">
            <BaseSwitch
              v-model="formData.enableBudgetAutoSave"
              label="Enable Budget Auto Save"
            />
            <BaseSwitch
              v-model="formData.enableProposalAutoSave"
              label="Enable Proposal Auto Save"
            />
          </div>

          <form.Field
            name="autoSaveWaitTime"
            :validators="{
              onBlur: yup.number().transform((value) => isNaN(Number(value)) ? -1 : Number(value)).required('Auto Save Wait Time is required').min(1, 'Must be greater than or equal to 1').max(60, 'Must be less than or equal to 60'),
            }"
          >
            <template #default="{ field }">
              <BaseInput
                v-model.number="formData.autoSaveWaitTime"
                :name="field.name"
                type="number"
                step="1"
                label="Auto Save Wait Time (seconds)"
                placeholder="Auto Save Wait Time (seconds)"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <hr class="mb-4 border-slate-200 border-b" />
          <div class="flex justify-end">
            <form.Subscribe>
              <template #default="{ canSubmit, isSubmitting }">
                <BaseButton
                  variant="primary"
                  type="submit"
                  :disabled="!canSubmit || isSubmitting"
                  :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
                >
                  <div class="flex justify-between items-center gap-2">
                    <span>Save</span>
                    <span
                      v-if="isSubmitting"
                      class="i-heroicons-arrow-path animate-spin"
                    />
                  </div>
                </BaseButton>
              </template>
            </form.Subscribe>
          </div>
        </div>
      </div>
    </form>
  </div>

  <EmptyState v-else>
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the general settings.
      </p>
    </template>
  </EmptyState>
</template>

<script lang="ts" setup>
import { ref, watch, } from 'vue'
import BaseInput from '@/components/base/BaseInput.vue'
import Editor from '@/components/TextEditor.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useHead } from '@unhead/vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import SettingService from '@/services/SettingService'
import { useNotification } from '@kyvg/vue3-notification'
import EmptyState from '@/components/app/EmptyState.vue'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { useForm } from '@tanstack/vue-form'
import BaseSwitch from '@/components/base/BaseSwitch.vue'

useHead({ title: 'General Settings' })

const { notify } = useNotification()
const formData = ref({
  companyAddress: '',
  defaultMargin: 30,
  defaultTax: 6,
  contractTerms: '',
  enableBudgetAutoSave: true,
  enableProposalAutoSave: true,
  autoSaveWaitTime: 10, // in seconds
})

const queryClient = useQueryClient()

const { data, isPending, isError } = useQuery({
  queryKey: ['settings', 'generalSettings'],
  queryFn: async () => {
    const settings = await new SettingService().find('generalSettings')
    return settings
  }
})

const form = useForm({
  defaultValues: formData.value,
  validatorAdapter: yupValidator(),
  onSubmit: async () => await saveSettings()
})

watch(isPending, (loading) => {
  if (!loading && !isError.value) {
    formData.value.companyAddress = data.value.companyAddress || ''
    formData.value.defaultMargin = data.value.defaultMargin || 30
    formData.value.defaultTax = data.value.defaultTax || 6
    formData.value.contractTerms = data.value.contractTerms || ''
    formData.value.enableBudgetAutoSave = Object.hasOwn(data.value, 'enableBudgetAutoSave') ? data.value.enableBudgetAutoSave : true
    formData.value.enableProposalAutoSave = Object.hasOwn(data.value, 'enableProposalAutoSave') ? data.value.enableProposalAutoSave : true
    formData.value.autoSaveWaitTime = data.value.autoSaveWaitTime || 10
    form.reset()
  }
}, { immediate: true })

const { mutateAsync: saveSettings } = useMutation({
  mutationFn: async () => {
    await new SettingService().update('generalSettings', formData.value)
    queryClient.setQueryData(['settings', 'generalSettings'], formData.value)
  },
  onSuccess: () => {
    notify({ title: 'Success', text: 'Setting saved!', type: 'success' })
  },
  onError: (error) => {
    console.error(error)
    notify({ title: 'Error', text: error.message, type: 'error' })
  }
})

</script>
